/**
 * svgProbas — visuels du dossier probabilités (5e, thème #7).
 *
 * Figures STATIQUES d'énoncé générées par construction depuis les données du
 * tirage (source de vérité unique, jamais d'image figée) : urne de boules
 * numérotées, roue de loterie à 8 secteurs égaux, échelle de probabilité
 * 0 → 1. Les diagrammes (barres, ligne brisée), tableaux HTML et emballages
 * imgElement/imgChoix sont réutilisés depuis ../statistiques/svgStats.
 */
import { shuffle } from '../../../utils';

// ─── Couleurs des boules/billes (adjectifs féminins singuliers) ───

export const COULEURS_OBJETS = {
    rouge:    { fill: '#dc2626', texte: '#fff' },
    bleue:    { fill: '#1d4ed8', texte: '#fff' },
    verte:    { fill: '#16a34a', texte: '#fff' },
    jaune:    { fill: '#eab308', texte: '#1e293b' },
    violette: { fill: '#7c3aed', texte: '#fff' },
    noire:    { fill: '#1e293b', texte: '#fff' },
};

/**
 * svgUrne(boules) — urne transparente contenant 8 boules numérotées.
 * `boules` : [{ couleur (clé de COULEURS_OBJETS), numero }] (longueur 8).
 */
export const svgUrne = (boules) => {
    const W = 300, H = 196;
    const vase = '<path d="M 34 34 C 34 26, 60 20, 150 20 C 240 20, 266 26, 266 34 '
        + 'L 258 168 C 256 182, 44 182, 42 168 Z" fill="#f1f5f9" stroke="#334155" stroke-width="2.5"/>';
    let cercles = '';
    boules.forEach((b, i) => {
        const cx = 66 + (i % 4) * 56;
        const cy = i < 4 ? 88 : 142;
        const c = COULEURS_OBJETS[b.couleur];
        cercles += `<circle cx="${cx}" cy="${cy}" r="22" fill="${c.fill}" stroke="#0f172a" stroke-width="1.5"/>`
            + `<text x="${cx}" y="${cy + 5}" font-size="15" font-weight="bold" fill="${c.texte}" text-anchor="middle">${b.numero}</text>`;
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">${vase}${cercles}</svg>`;
};

/**
 * svgRoue(secteurs) — roue de loterie à 8 secteurs ÉGAUX + flèche fixe.
 * `secteurs` : les 8 gains AFFICHÉS (strings déjà formatées, ex. "5 000").
 */
export const svgRoue = (secteurs) => {
    const r = 92, cx = 112, cy = 122;
    const W = 224, H = 226;
    const PALETTE = ['#1E3A8A', '#b45309', '#0e7490', '#9d174d', '#15803d', '#475569', '#7c3aed', '#be123c'];
    let dessin = '';
    secteurs.forEach((g, i) => {
        const a0 = ((i * 45 - 90) * Math.PI) / 180;
        const a1 = (((i + 1) * 45 - 90) * Math.PI) / 180;
        const mid = ((i * 45 + 22.5 - 90) * Math.PI) / 180;
        const x0 = (cx + r * Math.cos(a0)).toFixed(1), y0 = (cy + r * Math.sin(a0)).toFixed(1);
        const x1 = (cx + r * Math.cos(a1)).toFixed(1), y1 = (cy + r * Math.sin(a1)).toFixed(1);
        dessin += `<path d="M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 0 1 ${x1} ${y1} Z" fill="${PALETTE[i]}" stroke="#fff" stroke-width="2"/>`;
        const lx = (cx + 0.64 * r * Math.cos(mid)).toFixed(1);
        const ly = (cy + 0.64 * r * Math.sin(mid) + 4).toFixed(1);
        dessin += `<text x="${lx}" y="${ly}" font-size="11" font-weight="bold" fill="#fff" text-anchor="middle">${g}</text>`;
    });
    dessin += `<circle cx="${cx}" cy="${cy}" r="7" fill="#1e293b"/>`
        + `<path d="M ${cx - 9} 8 L ${cx + 9} 8 L ${cx} 26 Z" fill="#1e293b"/>`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">${dessin}</svg>`;
};

/**
 * svgEchelle() — échelle de probabilité de 0 à 1 (repères 0, 1/6, 1/2, 1 et
 * zones « très proche de 0 / de 1 »). Support visuel : ne révèle la position
 * d'AUCUN événement.
 */
export const svgEchelle = () => {
    const y = 52, x0 = 40, x1 = 380;
    const xDe = (f) => x0 + f * (x1 - x0);
    const trait = (x) => `<line x1="${x}" y1="${y - 8}" x2="${x}" y2="${y + 8}" stroke="#334155" stroke-width="2"/>`;
    const tick = (f, label) => {
        const x = xDe(f).toFixed(1);
        return trait(x)
            + `<text x="${x}" y="${y + 26}" font-size="13" fill="#1e293b" text-anchor="middle">${label}</text>`;
    };
    // Graduation en fraction VERTICALE dessinée (pas de KaTeX dans un SVG) :
    // numérateur, barre, dénominateur (audit fractions en texte, 2026-09-06 ;
    // « 1/6 » en texte ne se lit pas comme une fraction en 5e).
    const tickFrac = (f, num, den) => {
        const x = xDe(f).toFixed(1);
        const xn = Number(x);
        return trait(x)
            + `<text x="${x}" y="${y + 24}" font-size="12" fill="#1e293b" text-anchor="middle">${num}</text>`
            + `<line x1="${(xn - 6).toFixed(1)}" y1="${y + 28}" x2="${(xn + 6).toFixed(1)}" y2="${y + 28}" stroke="#1e293b" stroke-width="1.2"/>`
            + `<text x="${x}" y="${y + 40}" font-size="12" fill="#1e293b" text-anchor="middle">${den}</text>`;
    };
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 420 104" width="420" height="104" font-family="Arial">`
        + `<line x1="${x0}" y1="${y}" x2="${x1}" y2="${y}" stroke="#334155" stroke-width="2.5"/>`
        + tick(0, '0') + tickFrac(1 / 6, 1, 6) + tickFrac(0.5, 1, 2) + tick(1, '1')
        + `<text x="${x0}" y="${y - 16}" font-size="12" fill="#475569" text-anchor="start">impossible</text>`
        + `<text x="${x1}" y="${y - 16}" font-size="12" fill="#475569" text-anchor="end">certain</text>`
        + '</svg>';
};

/** Tire nbV lignes vraies + nbF fausses et mélange — pour makeVraiFaux. */
export const melangeVF = (vraies, fausses, nbV = 2, nbF = 2) => shuffle([
    ...shuffle([...vraies]).slice(0, nbV).map((t) => [t, 0]),
    ...shuffle([...fausses]).slice(0, nbF).map((t) => [t, 1]),
]);
